import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import useResultsMetadata from '@Pages/Results/hooks/useResultsMetadata';
import HeatmapHelperService from '@Pages/Heatmap/HeatmapHelperService';
import { cellOperation as cellOperationSelector } from '@Pages/Heatmap/selectors';
import ResultsInterface from '@Services/ResultsInterface';

export const INITIAL_LIMIT = 5;
export const LOAD_MORE_LIMIT = 10;
export const CELL_OPERATION_MAPPING = {
  MAX: 'Maximum',
  MIN: 'Minimum',
  AVERAGE: 'Average',
  MEDIAN: 'Median',
};

function getPxStyle(px) {
  // eslint-disable-next-line no-param-reassign
  px = parseFloat(px) * 10;
  return {
    color: HeatmapHelperService.getColorBypX(px),
    backgroundColor: HeatmapHelperService.getBgColorBypX(px),
  };
}

function useCellDetailsResults({ matrixName, params, cellData }) {
  const metadata = useResultsMetadata(params);
  const [isLoading, setIsLoading] = useState(false);
  const [extendedMetadata, setExtendedMetadata] = useState(metadata);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [firstItem, setFirstItem] = useState(1);
  const [lastItem, setLastItem] = useState(INITIAL_LIMIT);
  const [resultSet, setResultSet] = useState(null);
  const [resultsItems, setResultsItems] = useState([]);
  const cellOperation = useSelector(cellOperationSelector);

  function handleResults(items) {
    const formattedItems = items.map((item) => {
      const px = Math.floor10(item.pxValue, -1);
      return {
        ...item,
        px,
        pxColor: getPxStyle(px),
        cellOperation: CELL_OPERATION_MAPPING[cellOperation],
      };
    });

    setResultsItems(resultsItems.concat(formattedItems));

    setIsLoading(false);
  }

  // eslint-disable-next-line no-shadow
  function getSubstancesList({ resultName, databaseId, context, hitSetKey, firstItem, lastItem }) {
    ResultsInterface.getResults({
      resultName,
      databaseId,
      context,
      hitSetKey,
      firstItem,
      lastItem,
      isHeatmapCellDetails: true,
    }).subscribe({ next: ({ data }) => handleResults(data), error: Logger.error });
  }

  useEffect(() => {
    const { row, column } = cellData;

    setIsLoading(true);

    // Get result set
    MessageBroker.send(RequestUrls.heatmapCell, {
      matrixName,
      // Back end indexes compensation
      column: column + 1,
      row: row + 1,
    })
      .then((response) => {
        const { resultName, databaseId } = response;

        // Extend React metadata
        setExtendedMetadata({
          ...metadata,
          nestedResultName: resultName,
          nestedDatabaseId: databaseId,
          heatmapMode: true,
        });

        setResultSet(response);
      })
      .catch(Logger.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellData]);

  useEffect(() => {
    if (!resultSet) return;

    const { databaseId, context, resultName, hitSetKey, resultSize } = resultSet;

    setIsLoading(true);
    setShowLoadMore(resultSize > lastItem);

    // Get results items
    getSubstancesList({ resultName, databaseId, context, hitSetKey, firstItem, lastItem });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultSet, firstItem, lastItem]);

  const loadMore = useCallback(() => {
    setFirstItem(lastItem + 1);
    setLastItem(lastItem + LOAD_MORE_LIMIT);
  }, [lastItem]);

  return {
    isLoading,
    metadata: extendedMetadata,
    context: get(resultSet, 'context'),
    totalResults: get(resultSet, 'resultSize'),
    resultsItems,
    showLoadMore,
    loadMore,
  };
}

export default useCellDetailsResults;
